"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  initJourney,
  getJourneyEventParams,
  flushJourneyUpdates,
} from "@/utils/journeyTracking";
import {
  trackEvent,
  trackEngagement,
  isGtagAvailable,
  initializeUserIdCache,
} from "@/utils/analytics";
import { queueAnalyticsOperation } from "@/utils/analyticsQueue";
import { logger } from "@/utils/logger";

export default function AnalyticsSessionInit() {
  const pathname = usePathname();
  const { setLandingUserProps } = useAnalytics();

  useEffect(() => {
    // Initialize user ID cache early to ensure supabase_user_id is available
    // for all events, including early lifecycle events like tutorial_step
    initializeUserIdCache();
    // Build landing attribution properties
    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();
    const utm_source = params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");

    const referrer = typeof window !== "undefined" ? document.referrer : null;
    let refSource: string | null = null;
    try {
      const ref = referrer ? new URL(referrer) : null;
      refSource = ref ? ref.hostname : null;
    } catch {
      refSource = null;
    }

    const landingPage = pathname || "/";

    // Initialize journey tracking
    initJourney(landingPage, utm_source || refSource, utm_medium, utm_campaign);

    // Set GA4 user properties
    setLandingUserProps({
      landing_page: landingPage,
      landing_source: utm_source || refSource,
      landing_medium: utm_medium,
      landing_campaign: utm_campaign,
    });

    // For manual campaign tagging (UTM parameters), explicitly set source/medium/campaign
    // Otherwise, let GA4 automatically detect from referrer, gclid, etc.
    // GA4 will automatically handle:
    // - Google Ads (via gclid parameter in URL)
    // - Organic search (from referrers like Google, Bing, etc.)
    // - Referral traffic (from other domains)
    // - Direct traffic (when no referrer exists)
    let ga4Source: string | undefined;
    let ga4Medium: string | undefined;
    let ga4Campaign: string | undefined;

    if (utm_source) {
      // Manual campaign tagging - set explicitly so GA4 uses these values
      ga4Source = utm_source;
      ga4Medium = utm_medium || undefined;
      ga4Campaign = utm_campaign || undefined;

      // Set at GA4 config level before first page_view for first user attribution
      if (isGtagAvailable()) {
        try {
          const ga4Config: Record<string, string> = {
            source: ga4Source,
            medium: ga4Medium || "(not set)",
          };
          if (ga4Campaign) {
            ga4Config.campaign = ga4Campaign;
          }
          window.gtag("set", ga4Config);
        } catch (error) {
          logger.warn("Failed to set GA4 source parameters", {
            component: "AnalyticsSessionInit",
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }
    }
    // If no UTM parameters, let GA4 automatically detect from referrer and gclid

    // Track enhanced page view with journey context and source attribution
    const journeyParams = getJourneyEventParams();
    trackEvent("page_view", {
      ...journeyParams,
      page_title: document.title,
      page_location:
        typeof window !== "undefined" ? window.location.href : landingPage,
      // Only include source/medium/campaign if we have explicit UTM parameters
      // GA4 will automatically detect everything else
      ...(ga4Source && { source: ga4Source }),
      ...(ga4Medium && { medium: ga4Medium }),
      ...(ga4Campaign && { campaign: ga4Campaign }),
    });

    // Track landing interaction after a short delay to capture first meaningful interaction
    let interactionTracked = false;
    let hasInteracted = false;
    const pageStartTime = Date.now();

    const trackFirstInteraction = () => {
      if (interactionTracked) return;
      interactionTracked = true;
      hasInteracted = true;

      // Queue analytics to avoid blocking interaction
      queueAnalyticsOperation(() => {
        const journeyParams = getJourneyEventParams();
        trackEvent("landing_interaction", {
          ...journeyParams,
          interaction_type: "scroll",
          page_location:
            typeof window !== "undefined" ? window.location.href : landingPage,
        });
      });
    };

    // Track scroll as first interaction
    const handleScroll = () => {
      if (window.scrollY > 100) {
        trackFirstInteraction();
        window.removeEventListener("scroll", handleScroll);
      }
    };

    // Track click as first interaction
    const handleClick = () => {
      trackFirstInteraction();
      document.removeEventListener("click", handleClick);
    };

    // Set timeout to track interaction if user doesn't interact
    const timeoutId = setTimeout(() => {
      trackFirstInteraction();
    }, 5000);

    // Standard GA4 engagement tracking:
    // Accumulate visible time and dispatch 'user_engagement' with 'engagement_time_msec'
    // on visibility changes and on a low-frequency heartbeat (60s) to avoid spam.
    let visibleSince: number | null =
      typeof document !== "undefined" && document.visibilityState === "visible"
        ? Date.now()
        : null;
    let accumulatedVisibleMs = 0;

    const sendUserEngagement = (
      reason: "heartbeat" | "hidden" | "pagehide"
    ) => {
      // Only send if there is at least 1s of engagement accumulated
      if (accumulatedVisibleMs >= 1000) {
        trackEvent("user_engagement", {
          engagement_time_msec: Math.round(accumulatedVisibleMs),
          engagement_reason: reason,
        });
        accumulatedVisibleMs = 0;
      }
    };

    const handleVisibilityChange = () => {
      const now = Date.now();
      if (document.visibilityState === "hidden") {
        if (visibleSince != null) {
          accumulatedVisibleMs += now - visibleSince;
          visibleSince = null;
        }
        sendUserEngagement("hidden");
        // Flush journey updates when page becomes hidden
        flushJourneyUpdates();
      } else {
        // Became visible
        visibleSince = now;
      }
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      // If page is being restored from bfcache, don't send analytics (bfcache preserves state)
      if (event.persisted) {
        logger.debug("Page restored from bfcache, skipping analytics flush", {
          component: "AnalyticsSessionInit",
        });
        return;
      }

      const now = Date.now();
      if (visibleSince != null) {
        accumulatedVisibleMs += now - visibleSince;
        visibleSince = null;
      }
      sendUserEngagement("pagehide");

      // Minimal bounce signal: only when user leaves within 10s without interaction
      const timeOnPage = Math.round((Date.now() - pageStartTime) / 1000);
      if (!hasInteracted && timeOnPage < 10) {
        trackEngagement("bounce", timeOnPage);
      }

      // Flush journey updates on page hide (only when not persisted)
      flushJourneyUpdates();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // Page was restored from bfcache - reset timing and don't send analytics
      if (event.persisted) {
        logger.debug("Page restored from bfcache", {
          component: "AnalyticsSessionInit",
        });
        // Reset visible time tracking for bfcache restoration
        visibleSince =
          typeof document !== "undefined" &&
          document.visibilityState === "visible"
            ? Date.now()
            : null;
        accumulatedVisibleMs = 0;
        // Reset interaction tracking for restored page
        interactionTracked = false;
        hasInteracted = false;
      }
    };

    // Heartbeat every 120s while visible to reduce analytics overhead
    const heartbeatInterval = setInterval(() => {
      if (typeof document === "undefined") return;
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (visibleSince != null) {
        accumulatedVisibleMs += now - visibleSince;
        visibleSince = now;
      }
      sendUserEngagement("heartbeat");
    }, 120000);

    if (typeof window !== "undefined") {
      // Not `{ once: true }`: the handler only counts a scroll past 100px and
      // removes itself when that happens — once-only would drop the listener
      // on the first tiny scroll and never track the real one.
      window.addEventListener("scroll", handleScroll, { passive: true });
      document.addEventListener("click", handleClick, { once: true });
      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("pagehide", handlePageHide);
      window.addEventListener("pageshow", handlePageShow);
    }

    return () => {
      clearTimeout(timeoutId);
      clearInterval(heartbeatInterval);
      if (typeof window !== "undefined") {
        window.removeEventListener("scroll", handleScroll);
        document.removeEventListener("click", handleClick);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.removeEventListener("pagehide", handlePageHide);
        window.removeEventListener("pageshow", handlePageShow);
      }
    };
    // Only run on first render for the session; guard handled in util
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
